/**
 * TrustGate Console - Proof Explorer
 * Search and view proof/request details by ID
 */

import React, { useState } from 'react';
import { Card } from './components/Card';
import { MetricRow } from './components/MetricRow';
import { ToastContainer, useToast } from './components/Toast';
import { BoundIntentDetailView } from './components/BoundIntentDetailView';
import {
  fetchPublicProof,
  fetchRequestById,
  formatCurrency,
  formatTimestamp,
} from '../data/TrustGateDataService';
import { approveRequest, denyRequest } from '../data/TrustGateRequestsService';
import { adaptProofAPIToEvent, adaptRequestToEvent } from '../ecs/adapters';
import type { TrustGateEvent } from '../ecs/types';

interface ProofExplorerProps {
  onNavigateBack: () => void;
  isAdmin?: boolean;
}

type SearchResult = TrustGateEvent | null;

export const ProofExplorer: React.FC<ProofExplorerProps> = ({ onNavigateBack, isAdmin = false }) => {
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [showDetailView, setShowDetailView] = useState(false);
  
  const { toasts, showToast, removeToast } = useToast();

  const handleSearch = async () => {
    const trimmedInput = searchInput.trim();
    
    if (!trimmedInput) {
      setSearchError('Please enter a Proof ID or Request ID');
      return;
    }

    setSearching(true);
    setSearchError(null);
    setSearchResult(null);

    try {
      // Determine ID type and search accordingly
      if (trimmedInput.startsWith('prf_')) {
        // Search for proof
        const response = await fetchPublicProof(trimmedInput);
        
        if (!response.ok || !response.proof) {
          if (response.error === 'NOT_FOUND') {
            setSearchError('Proof not found. The proof ID may not exist or may not be shareable.');
          } else if (response.error === 'EXPIRED_OR_REVOKED') {
            setSearchError('This proof has expired or been revoked.');
          } else {
            setSearchError('Failed to fetch proof. Please try again.');
          }
          return;
        }

        // Adapt proof to ECS event
        const event = adaptProofAPIToEvent(response.proof);
        setSearchResult(event);
      } else if (trimmedInput.startsWith('req_') || trimmedInput.length > 10) {
        // Search for request
        const request = await fetchRequestById(trimmedInput);
        
        if (!request) {
          setSearchError('Request not found. The request ID may not exist.');
          return;
        }

        // Adapt request to ECS event
        const event = adaptRequestToEvent(request);
        setSearchResult(event);
      } else {
        setSearchError('Invalid ID format. Expected prf_... (Proof ID) or req_... (Request ID)');
      }
    } catch (error) {
      console.error('[ProofExplorer] Search error:', error);
      setSearchError(error instanceof Error ? error.message : 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const copyVerifyUrl = (event: TrustGateEvent) => {
    if (!event.proofId) return;
    const baseUrl = 'https://trustgate-27ddc.web.app';
    const verifyUrl = `${baseUrl}/verify/${event.proofId}`;
    navigator.clipboard.writeText(verifyUrl);
    showToast('Verify URL copied to clipboard', 'success');
  };

  const openVerifyPage = (event: TrustGateEvent) => {
    if (!event.proofId) return;
    const baseUrl = 'https://trustgate-27ddc.web.app';
    const verifyUrl = `${baseUrl}/verify/${event.proofId}`;
    window.open(verifyUrl, '_blank');
  };

  const handleApprove = async (requestId: string) => {
    setActionInProgress(true);
    setActionSuccess(null);
    setSearchError(null);

    try {
      const response = await approveRequest(requestId, 'ROUTINE_APPROVAL');
      
      if (response.ok) {
        setActionSuccess(`Request approved. Execution will proceed automatically.`);
        showToast('Request approved. Execution will proceed automatically.', 'success');
        
        // Refresh the request to show updated status
        setTimeout(async () => {
          const request = await fetchRequestById(requestId);
          if (request) {
            const event = adaptRequestToEvent(request);
            setSearchResult(event);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('[ProofExplorer] Approve error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Binding failed';
      setSearchError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeny = async (requestId: string) => {
    const reason = prompt('Enter reason for denying binding (optional):');
    
    setActionInProgress(true);
    setActionSuccess(null);
    setSearchError(null);

    try {
      const response = await denyRequest(requestId, reason ? 'ADMIN_DENIED' : undefined, reason || undefined);
      
      if (response.ok) {
        setActionSuccess('Request denied.');
        showToast('Request denied.', 'info');
        
        // Refresh the request to show updated status
        setTimeout(async () => {
          const request = await fetchRequestById(requestId);
          if (request) {
            const event = adaptRequestToEvent(request);
            setSearchResult(event);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('[ProofExplorer] Deny error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Denial action failed';
      setSearchError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#f8fafc]">Proof Explorer</h1>
        <button
          onClick={onNavigateBack}
          className="px-4 py-2 text-[#cbd5e1] hover:text-[#f8fafc] transition-colors"
        >
          ← Back to Console
        </button>
      </div>

      {/* Search Bar */}
      <Card>
        <div className="flex gap-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter Proof ID (prf_...) or Request ID (req_...)"
            disabled={searching}
            className="flex-1 px-4 py-2 bg-[#0a0a0f] border border-white/10 rounded-lg text-[#f8fafc] placeholder-[#cbd5e1]/50 focus:outline-none focus:border-[#00d4aa] transition-colors"
          />
          <button
            onClick={handleSearch}
            disabled={searching}
            className="px-6 py-2 bg-[#00d4aa] hover:bg-[#00a8cc] text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>
      </Card>

      {/* Error State */}
      {searchError && (
        <Card error>
          <div className="text-center py-4">
            <div className="text-[#ef4444] text-lg mb-2">❌ {searchError}</div>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!searchResult && !searchError && !searching && (
        <Card>
          <div className="text-center py-12 text-[#cbd5e1]">
            <div className="text-4xl mb-4">🔍</div>
            <div className="text-lg mb-2">Enter a Proof ID or Request ID to begin</div>
            <div className="text-sm">
              Proof IDs start with <code className="px-2 py-1 bg-white/10 rounded font-mono">prf_</code>
            </div>
            <div className="text-sm">
              Request IDs start with <code className="px-2 py-1 bg-white/10 rounded font-mono">req_</code>
            </div>
          </div>
        </Card>
      )}

      {/* Event Details (Proof) */}
      {searchResult && searchResult.entityKind === 'proof' && (
        <Card title="Proof Details (ECS Event)" className="hover:shadow-xl transition-shadow duration-200">
          <div className="space-y-2">
            <MetricRow label="Event ID" value={searchResult.id} copyable mono />
            <MetricRow label="Event Type" value={searchResult.type} badge />
            <MetricRow label="Proof ID" value={searchResult.proofId} copyable mono />
            <MetricRow label="Entity ID" value={searchResult.entityId} copyable mono />
            <MetricRow label="Decision" value={searchResult.decision || '—'} badge={!!searchResult.decision} />
            <MetricRow label="Status" value={searchResult.status} badge />
            <MetricRow label="Timestamp" value={formatTimestamp(searchResult.timestamp)} />
            <MetricRow label="Actor" value={searchResult.actorHash || '—'} mono />
            <MetricRow label="Policy Hash" value={searchResult.policyHash || '—'} mono />
          </div>

          {searchResult.proofId && (
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => copyVerifyUrl(searchResult)}
                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-[#f8fafc] border border-white/10 rounded-lg font-medium transition-colors"
              >
                Copy Verify URL
              </button>
              <button
                onClick={() => openVerifyPage(searchResult)}
                className="flex-1 px-4 py-2 bg-[#00d4aa] hover:bg-[#00a8cc] text-white rounded-lg font-medium transition-colors"
              >
                Open Verify Page →
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Event Details (Request) */}
      {searchResult && searchResult.entityKind === 'request' && (
        <Card title="Request Details (ECS Event)" className="hover:shadow-xl transition-shadow duration-200">
          <div className="space-y-2">
            <MetricRow label="Event ID" value={searchResult.id} copyable mono />
            <MetricRow label="Event Type" value={searchResult.type} badge />
            <MetricRow label="Entity ID" value={searchResult.entityId} copyable mono />
            <MetricRow label="Status" value={searchResult.status} badge />
            {searchResult.decision && (
              <MetricRow label="Decision" value={searchResult.decision} badge />
            )}
            {searchResult.lockedParams && (
              <>
                <MetricRow 
                  label="Amount" 
                  value={formatCurrency(
                    searchResult.lockedParams.amount, 
                    searchResult.lockedParams.currency
                  )} 
                />
                <MetricRow label="Destination" value={searchResult.lockedParams.destination} />
                {searchResult.lockedParams.memo && (
                  <MetricRow label="Memo" value={searchResult.lockedParams.memo} />
                )}
              </>
            )}
            <MetricRow label="Actor" value={searchResult.actorEmail || searchResult.actorHash || '—'} />
            <MetricRow label="Timestamp" value={formatTimestamp(searchResult.timestamp)} />
            
            {searchResult.reasonCode && (
              <MetricRow label="Reason Code" value={searchResult.reasonCode} />
            )}

            {searchResult.proofId && (
              <MetricRow label="Proof ID" value={searchResult.proofId} copyable mono />
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6">
            {/* Success Message */}
            {actionSuccess && (
              <div className="p-4 bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-lg text-[#00d4aa] mb-4">
                ✓ {actionSuccess}
              </div>
            )}

            {/* View Full Record Button */}
            <div className="mb-4">
              <button
                onClick={() => setShowDetailView(true)}
                className="w-full px-4 py-3 bg-gradient-to-r from-[#00d4aa] to-[#00a8cc] hover:from-[#00a8cc] hover:to-[#008fa8] text-white rounded-lg font-semibold transition-colors shadow-lg"
              >
                🔒 View Full Lifecycle Record
              </button>
            </div>

            {/* Admin Actions for PENDING requests */}
            {isAdmin && searchResult.status === 'PENDING' && !searchResult.proofId && (
              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => handleApprove(searchResult.entityId)}
                  disabled={actionInProgress}
                  className="flex-1 px-4 py-2 bg-[#00d4aa] hover:bg-[#00a8cc] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionInProgress ? 'Processing...' : '🔒 Bind Intent'}
                </button>
                <button
                  onClick={() => handleDeny(searchResult.entityId)}
                  disabled={actionInProgress}
                  className="flex-1 px-4 py-2 bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionInProgress ? 'Processing...' : '⛔ Deny Binding'}
                </button>
              </div>
            )}

            {/* Proof Actions */}
            {searchResult.proofId && (
              <div className="flex gap-3">
                <button
                  onClick={() => copyVerifyUrl(searchResult)}
                  className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-[#f8fafc] border border-white/10 rounded-lg font-medium transition-colors"
                >
                  Copy Verify URL
                </button>
                <button
                  onClick={() => openVerifyPage(searchResult)}
                  className="flex-1 px-4 py-2 bg-[#00d4aa] hover:bg-[#00a8cc] text-white rounded-lg font-medium transition-colors"
                >
                  Open Verify Page →
                </button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-white/5">
        <div className="text-sm text-[#cbd5e1] space-y-2">
          <div className="flex items-start gap-2">
            <span className="text-[#00d4aa]">ℹ️</span>
            <div>
              <strong>Proof IDs</strong> are public identifiers for shareable proofs (format: prf_...)
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-[#00d4aa]">ℹ️</span>
            <div>
              <strong>Request IDs</strong> are internal identifiers for bound intents (format: req_...)
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-[#f59e0b]">⚠️</span>
            <div>
              Only <strong>shareable proofs</strong> can be viewed via Proof ID. Non-shareable proofs return "not found".
            </div>
          </div>
        </div>
      </Card>

      {/* Bound Intent Detail View Modal */}
      {searchResult && showDetailView && (
        <BoundIntentDetailView
          event={searchResult}
          onClose={() => setShowDetailView(false)}
          isAdmin={isAdmin}
        />
      )}
    </div>
    </>
  );
};
